import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React from 'react';

import { colors, componentTokens } from './KestrixDesignSystem.jsx';

/**
 * Kestrix TextField component with custom styling
 * 
 * @param {Object} props - Component props
 * @param {string} [props.variant] - Text field variant ('outlined', 'filled', 'standard')
 * @param {string} [props.size] - Text field size ('small', 'medium')
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.value - Field value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.required] - Whether field is required
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.type] - Input type
 * @param {React.ReactNode} [props.startIcon] - Icon to display at the start
 * @param {React.ReactNode} [props.endIcon] - Icon to display at the end
 * @param {boolean} props.error - Whether field is in error state
 * @param {Object} props.InputProps - Input props
 * @param {Object} props.sx - Additional styling
 * @returns {JSX.Element} Kestrix text field component
 */
const KestrixTextField = ({ 
  variant = 'outlined', 
  size = 'small', 
  placeholder, 
  value, 
  onChange, 
  required = false, 
  disabled = false, 
  type = 'text',
  startIcon,
  endIcon,
  InputProps = {}, 
  sx = {}, 
  ...props 
}) => {
  const tokens = componentTokens.input;
  
  // Prepare InputAdornment for start and end icons
  const inputProps = {
    ...InputProps,
    startAdornment: startIcon ? (
      <InputAdornment position="start">
        {startIcon}
      </InputAdornment>
    ) : InputProps.startAdornment,
    endAdornment: endIcon ? (
      <InputAdornment position="end">
        {endIcon}
      </InputAdornment>
    ) : InputProps.endAdornment,
  };
  
  return (
    <TextField
      variant={variant}
      size={size}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      type={type}
      InputProps={inputProps}
      sx={{ 
        width: '100%', 
        background: colors.background.primary, 
        borderRadius: tokens.borderRadius,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.border.light,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.primary[100],
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.primary[100],
        },
        '& .MuiInputBase-input': {
          fontSize: tokens.fontSize[size],
          color: colors.text.body,
        },
        '& .MuiInputLabel-root': {
          color: colors.text.subtitle,
          '&.Mui-focused': {
            color: colors.primary[100],
          },
        },
        '& .MuiInputAdornment-root': {
          color: colors.text.subtitle,
        },
        '& .MuiInputBase-root.Mui-disabled': {
          backgroundColor: colors.neutral[100],
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.neutral[200],
          },
          '& .MuiInputBase-input': {
            color: colors.text.disabled,
            WebkitTextFillColor: colors.text.disabled,
          },
          '& .MuiInputAdornment-root': {
            color: colors.text.disabled,
          },
        },
        ...sx
      }}
      {...props}
    />
  );
};

export default KestrixTextField; 